import { useState, useMemo, useEffect, useRef } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type FilterFn,
  type RowSelectionState,
} from '@tanstack/react-table'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Checkbox } from '../../lib/shadcn/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { cn } from '../../lib/shadcn/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../lib/shadcn/dropdown-menu'
import { RefreshCw, Download, Search, X, Printer, Plus, ImageIcon, Pencil, AlertTriangle, MoreHorizontal, ListChecks } from 'lucide-react'
import PrintLabelsDialog from './PrintLabelsDialog'
import ProductBulkEditDialog from './ProductBulkEditDialog'
import { WHOLESALE_DISCOUNTS, marginAt, wholesalePrice } from '../../utils/margins'
import { useGetProductCosts } from '../../hooks/backend/recipes'
import { useUpdateProductsBulk } from '../../hooks/backend/inventoryPage'
import { toast } from '../../lib/shadcn/sonner'

export interface ProductCost {
  sku: string
  has_recipe: boolean
  cost: number | null
  margin: number | null
  margin_pct: number | null
  incomplete: boolean
}

const money = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Product {
  sku: string
  item_name: string | null
  label_name: string | null
  weight_oz: number | null
  /** Measured bag weight; production and costs use it over weight_oz. */
  actual_weight_oz?: number | null
  description: string | null
  /** Long description for the online stores. */
  web_description?: string | null
  ingredients: string | null
  allergens: string | null
  price: number | null
  item_id: string | null
  qty_in_stock: number | null
  last_modified?: string | null
  qty_remake?: number | null
  qty_ordered?: number | null
  image_url?: string | null
  photos?: string[] | null
  categories?: string[] | null
  tags?: string[] | null
  publish_to?: 
  | string[]
  | string
  | null
  is_seasonal?: boolean | null
  available_start?: string | null
  available_end?: string | null
  wholesale_tier?: string | null
  // Unbagged popcorn on hand for the flavor (oz), Special Order stock.
  unbagged_oz?: number | string | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const nameOrSkuFilter: FilterFn<Product> = (row, _colId, filterVal: unknown) => {
  if (!filterVal) return true
  const v = String(filterVal).toLowerCase()
  return (
    (row.original.item_name ?? '').toLowerCase().includes(v) ||
    (row.original.sku ?? '').toLowerCase().includes(v)
  )
}

const CSV_KEYS: (keyof Product)[] = [
  'item_name', 'label_name', 'sku', 'item_id', 'price',
  'weight_oz', 'actual_weight_oz', 'qty_in_stock',
]

function downloadCSV(data: Product[]) {
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v)
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lines = [
    CSV_KEYS.join(','),
    ...data.map(p => CSV_KEYS.map(k => esc(p[k])).join(',')),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = 'products.csv'; a.click()
  URL.revokeObjectURL(url)
}

const emptyCell = <span className="text-muted-foreground/40 text-xs italic">—</span>

// ─── ProductTable component ───────────────────────────────────────────────────

// Click the number to edit it. Enter or clicking away saves, Esc cancels.
function StockCell({ value, onSave }: { value: number | null; onSave: (qty: number) => Promise<boolean> }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const cancelled = useRef(false)

  const start = () => {
    cancelled.current = false
    setDraft(value != null ? String(value) : '0')
    setEditing(true)
  }
  const commit = async () => {
    if (cancelled.current) return
    const qty = Number(draft)
    if (draft.trim() === '' || !Number.isInteger(qty) || qty < 0) {
      toast.error('Stock must be a whole number, 0 or more.')
      setEditing(false)
      return
    }
    if (qty === (value ?? 0)) {
      setEditing(false)
      return
    }
    setSaving(true)
    await onSave(qty)
    setSaving(false)
    setEditing(false)
  }

  if (editing) {
    return (
      <Input
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        autoFocus
        disabled={saving}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onFocus={e => e.target.select()}
        onBlur={() => void commit()}
        onKeyDown={e => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') {
            cancelled.current = true
            setEditing(false)
          }
        }}
        className="h-7 w-20 px-2 text-sm tabular-nums"
        aria-label="Qty in stock"
      />
    )
  }
  return (
    <button
      type="button"
      onClick={start}
      title="Click to change the stock count"
      className="min-w-[2.5rem] rounded border border-dashed border-transparent px-1.5 py-0.5 text-left text-sm tabular-nums hover:border-muted-foreground/40 hover:bg-muted"
    >
      {value != null ? String(value) : emptyCell}
    </button>
  )
}

interface ProductTableProps {
  products: Product[]
  loading: boolean
  onRefresh: () => void
  onAddProduct: () => void
  /** tab 'recipe' opens the product dialog on its Recipe tab. */
  onEditProduct: (product: Product, tab?: 'recipe') => void
}

export default function ProductTable({
  products,
  loading,
  onRefresh,
  onAddProduct,
  onEditProduct,
}: ProductTableProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'item_name', desc: false }])
  const [filterValue, setFilterValue] = useState('')
  const [printProduct, setPrintProduct] = useState<Product | null>(null)
  const [printOpen, setPrintOpen] = useState(false)
  // Rows ticked for Bulk Edit, keyed by SKU.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [bulkEditOpen, setBulkEditOpen] = useState(false)

  const getProductCosts = useGetProductCosts()
  const costRows = (getProductCosts.data as ProductCost[] | undefined) ?? []
  const costsBySku = useMemo(
    () => new Map(costRows.map(c => [c.sku, c])),
    [costRows],
  )
  // Qty In Stock is edited right in the table. Saved values show at once
  // and are dropped when the products reload with the new numbers.
  const updateProductsBulk = useUpdateProductsBulk()
  const [stockOverrides, setStockOverrides] = useState<Map<string, number>>(new Map())
  useEffect(() => { setStockOverrides(new Map()) }, [products])
  // Rows (and the Edit window) use the saved stock right away.
  const tableProducts = useMemo(
    () =>
      stockOverrides.size === 0
        ? products
        : products.map(p => (stockOverrides.has(p.sku) ? { ...p, qty_in_stock: stockOverrides.get(p.sku)! } : p)),
    [products, stockOverrides],
  )
  const saveStockRef = useRef<(sku: string, qty: number) => Promise<boolean>>(async () => false)
  saveStockRef.current = async (sku, qty) => {
    try {
      await updateProductsBulk.trigger({ changeset: [{ sku, qty_in_stock: qty }] })
      setStockOverrides(prev => new Map(prev).set(sku, qty))
      return true
    } catch (error) {
      toast.error(`Couldn't save stock for ${sku}: ${error instanceof Error ? error.message : 'Unknown error'}`)
      return false
    }
  }
  const refreshCosts = () => getProductCosts.trigger(undefined, { skipCache: true })
  // Reload costs whenever the products reload (e.g. after a product or
  // recipe save), so cost and margin stay current.
  const firstLoad = useRef(true)
  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false
      void getProductCosts.trigger()
    } else {
      void refreshCosts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products])

  const columns = useMemo<ColumnDef<Product>[]>(() => [
    {
      id: 'select',
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllRowsSelected()
              ? true
              : table.getIsSomeRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={value => table.toggleAllRowsSelected(!!value)}
          onClick={e => e.stopPropagation()}
          aria-label="Select all products"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
          aria-label={`Select ${row.original.item_name || row.original.sku}`}
        />
      ),
    },
    {
      id: 'photo',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const url = row.original.image_url
        return (
          <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded border bg-muted">
            {url ? (
              <img src={url} alt="" className="h-full w-full object-cover" />
            ) : (
              <ImageIcon className="h-4 w-4 text-muted-foreground/50" />
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'item_name',
      header: 'Item Name',
      cell: ({ row }) => {
        const name = row.original.item_name
        return (
          <button
            type="button"
            onClick={() => onEditProduct(row.original)}
            className="min-w-[8.5rem] text-left text-sm font-medium text-primary hover:underline"
          >
            {name && name !== '' ? name : row.original.sku}
          </button>
        )
      },
    },
    {
      accessorKey: 'qty_in_stock',
      header: 'Qty In Stock',
      cell: ({ row }) => {
        const sku = row.original.sku
        return <StockCell value={row.original.qty_in_stock} onSave={qty => saveStockRef.current(sku, qty)} />
      },
    },
    { accessorKey: 'qty_ordered',  header: 'Qty Ordered',  cell: ({ getValue }) => { const v = getValue() as number | null; return <span className="text-sm tabular-nums">{v != null && v > 0 ? String(v) : emptyCell}</span> } },
    { accessorKey: 'qty_remake',   header: 'Remake Qty',   cell: ({ getValue }) => { const v = getValue() as number | null; return <span className="text-sm tabular-nums">{v != null ? String(v) : emptyCell}</span> } },
    {
      id: 'cost',
      header: 'Cost',
      enableSorting: false,
      cell: ({ row }) => {
        const c = costsBySku.get(row.original.sku)
        if (!c || !c.has_recipe || c.cost == null) return emptyCell
        return (
          <div className="flex items-center gap-1 whitespace-nowrap">
            <span className="text-sm tabular-nums font-medium">{money(c.cost)}</span>
            {c.margin != null ? (
              <span
                className={cn(
                  'text-xs tabular-nums',
                  c.margin >= 0
                    ? 'text-emerald-600 dark:text-emerald-500'
                    : 'text-destructive',
                )}
              >
                ({c.margin_pct != null ? `${(c.margin_pct * 100).toFixed(0)}%` : money(c.margin)})
              </span>
            ) : null}
            {c.incomplete ? (
              <AlertTriangle
                className="h-3.5 w-3.5 text-amber-500"
                aria-label="Estimate — missing cost data"
              />
            ) : null}
          </div>
        )
      },
    },
    {
      id: 'wholesale_margin',
      header: () => (
        <span title="Profit and margin when sold at 30% off the list price">
          Margin @ 30% off
        </span>
      ),
      enableSorting: false,
      cell: ({ row }) => {
        const c = costsBySku.get(row.original.sku)
        const price = row.original.price
        if (!c || !c.has_recipe || c.cost == null || price == null) return emptyCell
        const at = wholesalePrice(price, WHOLESALE_DISCOUNTS[0])
        const m = marginAt(at, c.cost)
        if (!m) return emptyCell
        return (
          <div
            className="flex items-center gap-1 whitespace-nowrap"
            title={`Sold at ${money(at)}${c.incomplete ? ' (cost is an estimate)' : ''}`}
          >
            <span className="text-sm tabular-nums font-medium">{money(m.margin)}</span>
            {m.pct != null ? (
              <span
                className={cn(
                  'text-xs tabular-nums',
                  m.margin >= 0
                    ? 'text-emerald-600 dark:text-emerald-500'
                    : 'text-destructive',
                )}
              >
                ({(m.pct * 100).toFixed(0)}%)
              </span>
            ) : null}
          </div>
        )
      },
    },
    {
      id: 'labels',
      header: 'Labels',
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 whitespace-nowrap"
          onClick={() => { setPrintProduct(row.original); setPrintOpen(true) }}
        >
          <Printer className="w-3.5 h-3.5" /> Print
        </Button>
      ),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 whitespace-nowrap"
          onClick={() => onEditProduct(row.original)}
        >
          <Pencil className="w-3.5 h-3.5" /> Edit
        </Button>
      ),
    },
  ], [onEditProduct, costsBySku])

  const table = useReactTable({
    data: tableProducts,
    columns,
    state: { sorting, globalFilter: filterValue, rowSelection },
    onSortingChange: setSorting,
    getRowId: row => row.sku,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setFilterValue,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: nameOrSkuFilter,
  })

  // Ticked products that still exist (a reload can drop some).
  const selectedProducts = useMemo(
    () => tableProducts.filter(p => rowSelection[p.sku]),
    [tableProducts, rowSelection],
  )

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b">
        <h2 className="text-lg font-semibold">Products</h2>
        {/* Phones: filter, refresh, add and a "more" menu on one line. */}
        <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-wrap">
          <div className="relative flex-1 min-w-0 sm:flex-none">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Name or SKU…"
              value={filterValue}
              onChange={e => setFilterValue(e.target.value)}
              className="pl-8 h-8 w-full sm:w-52 text-sm"
            />
            {filterValue && (
              <button
                onClick={() => setFilterValue('')}
                aria-label="Clear filter"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {/* Phones: only shown once products are ticked. */}
          <Button
            variant="outline"
            size="sm"
            className={cn('gap-1.5', selectedProducts.length === 0 && 'hidden sm:inline-flex')}
            disabled={selectedProducts.length === 0}
            onClick={() => setBulkEditOpen(true)}
            title={selectedProducts.length === 0 ? 'Tick products to edit them together' : undefined}
            aria-label="Bulk Edit"
          >
            <ListChecks className="w-4 h-4" />
            <span className="hidden sm:inline">Bulk Edit</span>
            {selectedProducts.length > 0 ? ` (${selectedProducts.length})` : ''}
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadCSV(tableProducts)} className="gap-1.5 hidden sm:inline-flex">
            <Download className="w-4 h-4" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => { onRefresh(); refreshCosts() }} className="gap-1.5" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button size="sm" onClick={onAddProduct} className="gap-1.5" aria-label="Add Product">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add Product</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="sm:hidden" aria-label="More actions">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => downloadCSV(tableProducts)}>
                <Download className="w-4 h-4 mr-2" /> Download CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32 text-muted-foreground">
          Loading products…
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card">
              {table.getHeaderGroups().map(hg => (
                <TableRow key={hg.id}>
                  {hg.headers.map(header => (
                    <TableHead
                      key={header.id}
                      className="whitespace-nowrap text-xs select-none cursor-pointer"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === 'asc' ? ' ↑'
                        : header.column.getIsSorted() === 'desc' ? ' ↓' : ''}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center text-muted-foreground py-8">
                    No products found
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row, i) => (
                  <TableRow
                    key={row.id}
                    className={cn(i % 2 === 1 && 'bg-muted/40')}
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="py-4 px-2">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <ProductBulkEditDialog
        products={selectedProducts}
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        onSaved={() => {
          setRowSelection({})
          onRefresh()
        }}
      />

      <PrintLabelsDialog
        product={printProduct}
        open={printOpen}
        onOpenChange={setPrintOpen}
      />
    </div>
  )
}
